import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { LS_HAS_LANGUAGE_SCREEN } from '@/constants/common';
import { ROUTE } from '@/constants/routes';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import i18n from '@/i18n/i18n';

import { selectTerritoriesData } from '@/redux/countries/selectors';

import { Tenant } from '@/types/common';
import { Language } from '@/types/i18n';
import { OperationType } from '@/types/user';

import { removeLocal, saveLocal } from '@/utils/browser';
import { saveOperationType } from '@/utils/operationType';

import { validateRemediationCustomer } from '../api';

export const useValidateRemediation = () => {
  const navigate = useNavigate();
  const territories = useAppSelector(selectTerritoriesData);
  const { switchTenant } = useTranslate();
  const mutation = useMutation({
    mutationFn: validateRemediationCustomer,
    onSuccess: async response => {
      saveOperationType(OperationType.Remediation);
      const { tenant, email, language } = response;

      const countryCode =
        territories.find(item => item.tenant === tenant.toLowerCase())
          ?.countryCode || '';

      const updatedForm = {
        email: email,
        countryCode,
      };

      if (updatedForm.email) {
        if (tenant === Tenant.Suriname && !language) {
          saveLocal(LS_HAS_LANGUAGE_SCREEN, true);
          navigate(ROUTE.LANGUAGE_SELECTION, {
            state: {
              from: ROUTE.LOGIN,
              form: { email: email, countryCode: countryCode },
              type: OperationType.Remediation,
              tenant: tenant,
            },
          });
          return;
        }
        if (language) {
          switchTenant(tenant.toLowerCase() as Tenant);
          i18n.changeLanguage(language as Language);
        }
        navigate(ROUTE.LOGIN_VERIFY_EMAIL, {
          state: {
            from: ROUTE.LOGIN,
            form: updatedForm,
            type: OperationType.Remediation,
            tenant: tenant,
          },
        });
      }
    },
    onError: () => {
      navigate(ROUTE.SESSION_EXPIRED);
    },
  });

  useEffect(() => {
    removeLocal(LS_HAS_LANGUAGE_SCREEN);
  }, []);

  return mutation;
};
